import {
  Flag,
  countryName,
  formatWhole,
  getShared,
  getView,
  NotFound,
  ShareRefused,
  type Money,
  type TripView as View,
} from '@trippath/shared';
import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { day } from '../../lib/format';
import { viewMap } from '../../lib/map';
import { MapPanel } from '../../components/map/MapPanel';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ended' }
  | { kind: 'locked' }
  | { kind: 'phrase'; wrong: boolean }
  | { kind: 'ready'; view: View };

/** Where a shared link's phrase is kept while the tab is open: said once, not again. */
const phraseKey = (token: string) => `trippath-share-${token}`;

function keptPhrase(token: string): string | undefined {
  try {
    return sessionStorage.getItem(phraseKey(token)) ?? undefined;
  } catch {
    return undefined;
  }
}

function keepPhrase(token: string, phrase: string): void {
  try {
    sessionStorage.setItem(phraseKey(token), phrase);
  } catch {
    // Not kept: it is asked again on the next load.
  }
}

/** What a refusal says the page is. */
function refusedState(error: unknown): State {
  if (!(error instanceof ShareRefused)) {
    return { kind: error instanceof NotFound ? 'missing' : 'failed' };
  }
  switch (error.reason) {
    case 'gone':
      return { kind: 'missing' };
    case 'ended':
    case 'locked':
      return { kind: error.reason };
    case 'phrase':
    case 'wrong':
      return { kind: 'phrase', wrong: error.reason === 'wrong' };
    default:
      return { kind: 'failed' };
  }
}

/** The phrase a shared link asks. */
function PhraseForm({
  wrong,
  onPhrase,
}: {
  wrong: boolean;
  onPhrase: (phrase: string) => void;
}) {
  const { t } = useTranslation();
  const [phrase, setPhrase] = useState('');
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    onPhrase(phrase);
  };
  return (
    <div className="page">
      <form className="panel view-phrase" onSubmit={submit}>
        <h1>{t('view.phraseTitle')}</h1>
        <p className="quiet">{t('view.phraseLead')}</p>
        <label className="field">
          <span className="field-label">{t('view.phrase')}</span>
          <input
            className="text-input"
            autoComplete="off"
            required
            value={phrase}
            onChange={(event) => {
              setPhrase(event.target.value);
            }}
          />
        </label>
        {wrong && (
          <p className="field-error" role="alert">
            {t('view.phraseWrong')}
          </p>
        )}
        <button type="submit" className="button button-primary">
          {t('view.open')}
        </button>
      </form>
    </div>
  );
}

/**
 * A trip's route and dates, read only: what a viewer sees, and never an amount. With `shared`,
 * the trip of a read-only link, for anyone who has it.
 */
export function TripView({ shared = false }: { shared?: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '', token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [phrase, setPhrase] = useState(() => (shared ? keptPhrase(token) : undefined));
  // Each phrase said is asked anew, the same wrong one again too.
  const [said, setSaid] = useState(0);

  useEffect(() => {
    (shared ? getShared(token, phrase) : getView(id)).then(
      (view) => {
        if (shared && phrase) keepPhrase(token, phrase);
        setState({ kind: 'ready', view });
      },
      (error: unknown) => {
        setState(refusedState(error));
      },
    );
  }, [id, token, shared, phrase, said]);
  const data = useMemo(
    () => (state.kind === 'ready' ? viewMap(state.view) : { points: [], lines: [] }),
    [state],
  );

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind === 'phrase') {
    return (
      <PhraseForm
        wrong={state.wrong}
        onPhrase={(next) => {
          setPhrase(next);
          setSaid((n) => n + 1);
        }}
      />
    );
  }
  if (state.kind !== 'ready') {
    const said = {
      missing: shared ? 'view.gone' : 'trips.notFound',
      ended: 'view.ended',
      locked: 'view.locked',
      failed: 'trips.failed',
    }[state.kind];
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(said)}
        </p>
        {!shared && <Link to="/">{t('trips.back')}</Link>}
      </div>
    );
  }

  const { view } = state;
  const money = view.amounts;
  const expected = new Map(money?.stops.map((one) => [one.id, one.expected]));
  return (
    <div className="page">
      {!shared && (
        <Link className="back" to="/">
          {t('trips.back')}
        </Link>
      )}
      <div className="page-head">
        <div>
          <h1>{view.trip.name}</h1>
          <p className="quiet">
            {t('trips.dates', {
              from: day(view.trip.start, lang),
              to: day(view.trip.end, lang),
            })}
          </p>
        </div>
      </div>
      <p className="quiet">{t(money ? 'view.leadAmounts' : 'view.lead')}</p>
      {money && (
        <p className="view-amounts">
          {t('view.amounts', {
            expected: formatWhole(money.expected, lang),
            budget: formatWhole(money.budget, lang),
          })}
        </p>
      )}
      <section className="panel" aria-labelledby="view-route">
        <h2 id="view-route">{t('plan.route')}</h2>
        {view.stops.length === 0 ? (
          <p className="quiet">{t('view.noStops')}</p>
        ) : (
          <ul className="view-stops">
            {view.stops.map((stop) => (
              <li key={stop.id}>
                <span>
                  <Flag country={stop.country} />
                  <strong>{stop.city}</strong>, {countryName(stop.country, lang)}
                  {stop.unresolved && ` · ${t('view.undecided')}`}
                </span>
                <span className="quiet">
                  {t('trips.dates', { from: day(stop.start, lang), to: day(stop.end, lang) })} ·{' '}
                  {t('trips.days', { count: stop.days })}
                  {stop.mode && ` · ${t('view.then', { how: t(`mode.${stop.mode}`) })}`}
                  {expected.has(stop.id) &&
                    ` · ${formatWhole(expected.get(stop.id) as Money, lang)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        {view.travellers.map((traveller, at) => (
          <p key={`${String(at)}-${traveller.joins}`} className="quiet">
            {t('view.joins', {
              name: traveller.name ?? t('view.traveller', { n: at + 2 }),
              dates: t('trips.dates', {
                from: day(traveller.joins, lang),
                to: day(traveller.leaves, lang),
              }),
            })}
          </p>
        ))}
      </section>
      {view.files.length > 0 && (
        <section className="panel" aria-labelledby="view-files">
          <h2 id="view-files">{t('view.files')}</h2>
          <ul className="view-files">
            {view.files.map((file) => (
              <li key={file.id}>
                <a href={file.url} target="_blank" rel="noreferrer">
                  {file.name}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {view.journal.length > 0 && (
        <section className="panel" aria-labelledby="view-journal">
          <h2 id="view-journal">{t('view.journal')}</h2>
          {view.journal.map((one) => (
            <article key={one.day} className="view-journal-day">
              <h3>
                {day(one.day, lang)}
                {one.city && ` · ${one.city}`}
              </h3>
              {one.entry && (
                <>
                  {one.entry.title !== '' && (
                    <p className="view-journal-title">{one.entry.title}</p>
                  )}
                  {one.entry.body.split('\n').map((line, at) => (
                    <p key={`${String(at)} ${line}`}>{line}</p>
                  ))}
                </>
              )}
              {one.photos.length > 0 && (
                <ul className="journal-photos">
                  {one.photos.map((photo) => (
                    <li key={photo.id}>
                      <img
                        src={photo.image}
                        alt={photo.place ?? t('journal.photo')}
                        loading="lazy"
                      />
                      {photo.at && (
                        <span>
                          {photo.at}
                          {photo.place && ` · ${photo.place}`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </section>
      )}
      {data.points.length > 0 && (
        <section className="panel search-map" aria-label={t('map.title')}>
          <MapPanel
            data={data}
            label={t('map.label', { places: data.points.map((p) => p.label).join(' → ') })}
          />
        </section>
      )}
    </div>
  );
}
